import React, { useState } from "react";
import Typography from "@/ui/Typography/Typography";
import { useTranslation } from "react-i18next";
import { FlexRow, FlexColumn } from "@/ui/FlexContainer/FlexContainer";
import { ChatArrow } from "@/assets/icons";
import { PetsSurveysType } from "@/types/surveys";
import { useSurveysImagesStore } from "@/store/surveysImages";
import { useUserDataContext } from "@/contexts/UserContext";
import { useChatDataContext } from "@/contexts/ChatContext";
import cls from "./styles.module.scss";

interface PetsSurveysProps {
  petsSurveys: PetsSurveysType;
  setAttachBlockIsOpen: (par: boolean) => void;
}

const SurveysList = ({
  petsSurveys,
  setAttachBlockIsOpen,
}: PetsSurveysProps) => {
  const surveysImages = useSurveysImagesStore((state) => state.surveysImages);

  const { shareSurvey } = useChatDataContext();

  const sendSurvey = async (value: number) => {
    try {
      setAttachBlockIsOpen(false);
      await shareSurvey(value);
    } catch (e: any) {
      console.error(e);
    }
  };

  return (
    <FlexColumn gap={2}>
      {petsSurveys?.surveys.map((survey) => {
        const images = surveysImages.find((elem) => elem.id === survey.id);
        const isSurveyCompleted = survey.questions.find(
          (elem) => elem.answer === "TRUE"
        );

        return (
          isSurveyCompleted && (
            <FlexRow
              key={survey.id}
              justifyContent="space-between"
              className={cls.petsSurveys}
              onClick={() => sendSurvey(survey.response_id)}
            >
              <>
                <Typography
                  size="md"
                  lineHeight="24px"
                  color="var(--text-dark-primary)"
                >
                  {survey.title}
                </Typography>
                <div className={cls.petsSurveysImgBlock}>
                  <img src={images?.imgActive} alt="" />
                </div>
              </>
            </FlexRow>
          )
        );
      })}
    </FlexColumn>
  );
};

const PetsSurveys = ({
  petsSurveys,
  setAttachBlockIsOpen,
}: PetsSurveysProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const completedSurvey = petsSurveys.surveys.some((elem) =>
    elem.questions.find((item) => item.answer === "TRUE")
  );

  return (
    <>
      <FlexRow
        justifyContent="space-between"
        className={cls.petsSurveys}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Typography
          size="md"
          lineHeight="24px"
          color="var(--text-dark-primary)"
        >
          {petsSurveys.name}
        </Typography>
        <div className={isOpen ? cls.arrowIconOpen : cls.arrowIcon}>
          <ChatArrow />
        </div>
      </FlexRow>
      {isOpen &&
        (completedSurvey ? (
          <SurveysList
            petsSurveys={petsSurveys}
            setAttachBlockIsOpen={setAttachBlockIsOpen}
          />
        ) : (
          <FlexRow justifyContent="space-between" className={cls.noServeys}>
            <Typography
              size="md"
              lineHeight="24px"
              color="var(--text-dark-primary)"
            >
              Нет заполненных анкет
            </Typography>
          </FlexRow>
        ))}
    </>
  );
};

interface ShareSurveyBlockProps {
  setAttachBlockIsOpen: (par: boolean) => void;
}

export const ShareSurveyBlock = ({
  setAttachBlockIsOpen,
}: ShareSurveyBlockProps) => {
  const { t } = useTranslation();
  const { userData } = useUserDataContext();
  const { sharePet } = useChatDataContext();

  const completedSurvey = userData?.pets.some((pet) =>
    pet.surveys.some((elem) =>
      elem.questions.find((item) => item.answer === "TRUE")
    )
  );

  if (!userData) return null;

  const sendPet = async (petId: string) => {
    setAttachBlockIsOpen(false);
    await sharePet(petId);
  };

  return (
    <FlexColumn gap={8} className={cls.shareSurveyBlock}>
      <Typography
        size="xs"
        lineHeight="20px"
        color="var(--text-dark-primary)"
        className={cls.shareTitle}
      >
        Поделиться страницей питомца
      </Typography>
      <FlexColumn gap={2} className={cls.petShareList}>
        {userData.pets.map((pet) => (
          <button
            type="button"
            key={pet.id}
            className={cls.petShareItem}
            onClick={() => void sendPet(pet.id)}
          >
            <img
              src={pet.image || "/images/user_placeholder.png"}
              alt=""
              className={cls.petShareImage}
            />
            <span>{pet.name}</span>
          </button>
        ))}
      </FlexColumn>

      <div className={cls.shareDivider} />
      <Typography
        size="xs"
        lineHeight="20px"
        color="var(--text-dark-primary)"
        className={cls.shareTitle}
      >
        {t("chat.shareSurvey")}
      </Typography>
      {completedSurvey ? (
        <FlexColumn gap={2}>
          {userData.pets.length > 1 ? (
            userData.pets.map((pet) => (
              <PetsSurveys
                key={pet.id}
                petsSurveys={pet}
                setAttachBlockIsOpen={setAttachBlockIsOpen}
              />
            ))
          ) : (
            <SurveysList
              petsSurveys={userData.pets[0]}
              setAttachBlockIsOpen={setAttachBlockIsOpen}
            />
          )}
        </FlexColumn>
      ) : (
        <Typography
          size="sm"
          lineHeight="22px"
          color="var(--text-dark-secondary)"
          className={cls.shareEmpty}
        >
          Нет заполненных анкет
        </Typography>
      )}
    </FlexColumn>
  );
};
